/**
 * Painel de Notificações
 *
 * Exibe a lista de notificações do sistema em um dropdown.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, Package, CreditCard, Truck, AlertCircle, MessageSquare } from 'lucide-react';
import { clsx } from 'clsx';
import { useNotificationStore } from '@store/useNotificationStore';

/**
 * Props do componente
 */
interface NotificationPanelProps {
  /** Se o painel está aberto */
  isOpen: boolean;
  /** Callback para fechar o painel */
  onClose: () => void;
}

/**
 * Ícones por tipo de notificação
 */
const notificationIcons = {
  order: Package,
  payment: CreditCard,
  delivery: Truck,
  system: AlertCircle,
  message: MessageSquare,
};

/**
 * Cores por tipo de notificação
 */
const notificationColors = {
  order: 'text-primary-500 bg-primary-50 dark:bg-primary-900/30',
  payment: 'text-success-500 bg-success-50 dark:bg-success-900/30',
  delivery: 'text-warning-500 bg-warning-50 dark:bg-warning-900/30',
  system: 'text-error-500 bg-error-50 dark:bg-error-900/30',
  message: 'text-secondary-500 bg-secondary-50 dark:bg-secondary-900/30',
};

/**
 * Formata o tempo relativo
 */
function formatTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;

    return date.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
}

/**
 * Componente de painel de notificações
 */
export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
  } = useNotificationStore();

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Fecha com Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className={clsx(
        'absolute right-0 top-full z-50 mt-2 w-80 max-h-[70vh] overflow-hidden',
        'rounded-lg border border-gray-200 bg-white shadow-xl',
        'dark:border-gray-700 dark:bg-gray-800',
        'animate-in fade-in slide-in-from-top-2 duration-200',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Notificações
          </h3>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary-500 px-2 py-0.5 text-xs font-medium text-white">
              {unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="rounded p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              title="Marcar todas como lidas"
            >
              <CheckCheck size={16} />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Lista de notificações */}
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell size={32} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nenhuma notificação
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {notifications.map(notification => {
              const Icon = notificationIcons[notification.type];
              const colorClass = notificationColors[notification.type];

              return (
                <li
                  key={notification.id}
                  className={clsx(
                    'group relative flex gap-3 px-4 py-3',
                    'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                    'transition-colors duration-150',
                    !notification.read && 'bg-primary-50/50 dark:bg-primary-900/10',
                  )}
                >
                  {/* Ícone */}
                  <div
                    className={clsx(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                      colorClass,
                    )}
                  >
                    <Icon size={18} />
                  </div>

                  {/* Conteúdo */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={clsx(
                          'text-sm font-medium',
                          notification.read
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-gray-900 dark:text-white',
                        )}
                      >
                        {notification.title}
                      </p>
                      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(notification.createdAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {notification.message}
                    </p>
                  </div>

                  {/* Ações (visíveis no hover) */}
                  <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="rounded p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600"
                        title="Marcar como lida"
                      >
                        <Check size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => removeNotification(notification.id)}
                      className="rounded p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-200 hover:text-error-600 dark:hover:bg-gray-600"
                      title="Remover"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Indicador de não lida */}
                  {!notification.read && (
                    <div className="absolute left-1.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary-500" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
          <button
            onClick={clearAll}
            className="w-full rounded py-1.5 text-center text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            Limpar todas
          </button>
        </div>
      )}
    </div>
  );
}

export default NotificationPanel;
